// src/pages/booking/BookingAdminPage.tsx
// Provider / admin area: incoming requests, schedule, all bookings oversight,
// booking types, policies, numbering schemes.

import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Button, Badge, Alert } from '@react/components';
import { Select } from '@/components/Select';
import type { SelectOption } from '@/components/Select';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useScope } from '@/hooks/useScope';
import { BookingTypeDialog } from '@/features/booking/BookingTypeDialog';
import { NumberingSchemeDialog } from '@/features/booking/NumberingSchemeDialog';
import { PolicyEditor } from '@/features/booking/PolicyEditor';
import {
  BOOKING_STATUS_KEYS,
  BOOKING_STATUS_VARIANT,
  errMessage,
  fmtDateTime,
  fmtMoney,
} from '@/features/booking/lib';
import { ListProviderBookings } from '@core/api/endpoints/booking/list_provider_bookings';
import { ListBookings } from '@core/api/endpoints/booking/list_bookings';
import { ConfirmBooking } from '@core/api/endpoints/booking/confirm_booking';
import { RejectBooking } from '@core/api/endpoints/booking/reject_booking';
import { MarkNoShow } from '@core/api/endpoints/booking/mark_no_show';
import { ListBookingSchedule } from '@core/api/endpoints/booking/list_booking_schedule';
import { ListBookingTypes } from '@core/api/endpoints/booking/list_booking_types';
import { GetBookingType } from '@core/api/endpoints/booking/get_booking_type';
import { DeleteBookingType } from '@core/api/endpoints/booking/delete_booking_type';
import { ListBookingNumberingSchemes } from '@core/api/endpoints/booking/list_booking_numbering_schemes';
import { GetBookingNumberingScheme } from '@core/api/endpoints/booking/get_booking_numbering_scheme';
import { DeleteBookingNumberingScheme } from '@core/api/endpoints/booking/delete_booking_numbering_scheme';
import type { Booking, BookingNumberingScheme, BookingTypeDefinition, ScheduledBookingEntry } from '@core/types/booking';

type Tab = 'requests' | 'schedule' | 'all' | 'types' | 'policies' | 'numbering';
const TABS: Tab[] = ['requests', 'schedule', 'all', 'types', 'policies', 'numbering'];

export const BookingAdminPage = observer(() => {
  const { t, locale } = useI18n();
  const scope = useScope();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as Tab) || 'requests';
  const setTab = (tb: Tab) => setSearchParams({ tab: tb });

  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Requests
  const [pending, setPending] = useState<Booking[]>([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [rejecting, setRejecting] = useState<Booking | null>(null);

  // Schedule
  const [bookingTypes, setBookingTypes] = useState<BookingTypeDefinition[]>([]);
  const [scheduleTypeId, setScheduleTypeId] = useState('');
  const [schedule, setSchedule] = useState<ScheduledBookingEntry[]>([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  // All bookings
  const [allBookings, setAllBookings] = useState<Booking[]>([]);
  const [allLoading, setAllLoading] = useState(false);

  // Types
  const [typesLoading, setTypesLoading] = useState(false);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<BookingTypeDefinition | null>(null);
  const [deletingType, setDeletingType] = useState<BookingTypeDefinition | null>(null);

  // Numbering
  const [schemes, setSchemes] = useState<BookingNumberingScheme[]>([]);
  const [schemesLoading, setSchemesLoading] = useState(false);
  const [schemeDialogOpen, setSchemeDialogOpen] = useState(false);
  const [editingScheme, setEditingScheme] = useState<BookingNumberingScheme | null>(null);
  const [deletingScheme, setDeletingScheme] = useState<BookingNumberingScheme | null>(null);

  const loadTypes = useCallback(() => {
    setTypesLoading(true);
    return ListBookingTypes({ product_id: scope.productId, limit: 100 })
      .then((res) => setBookingTypes(res.definitions ?? []))
      .catch((e) => setError(errMessage(e, t('booking.admin.types.loadError'))))
      .finally(() => setTypesLoading(false));
  }, [scope.productId, t]);

  useEffect(() => {
    loadTypes();
  }, [loadTypes]);

  const loadPending = useCallback(() => {
    setPendingLoading(true);
    ListProviderBookings({ status: 'BOOKING_STATUS_PENDING_APPROVAL', limit: 100 })
      .then((res) => setPending(res.bookings ?? []))
      .catch((e) => setError(errMessage(e, t('booking.admin.requests.loadError'))))
      .finally(() => setPendingLoading(false));
  }, [scope.productId, t]);

  const loadSchedule = useCallback(() => {
    if (!scheduleTypeId) {
      setSchedule([]);
      return;
    }
    setScheduleLoading(true);
    const now = new Date();
    const in14 = new Date(now.getTime() + 14 * 86400000);
    ListBookingSchedule(scheduleTypeId, { time_min: now.toISOString(), time_max: in14.toISOString(), limit: 100 })
      .then((res) => setSchedule(res.entries ?? []))
      .catch((e) => setError(errMessage(e, t('booking.admin.schedule.loadError'))))
      .finally(() => setScheduleLoading(false));
  }, [scheduleTypeId, t]);

  const loadAll = useCallback(() => {
    setAllLoading(true);
    ListBookings({ limit: 100 })
      .then((res) => setAllBookings(res.bookings ?? []))
      .catch((e) => setError(errMessage(e, t('booking.admin.all.loadError'))))
      .finally(() => setAllLoading(false));
  }, [scope.productId, t]);

  const loadSchemes = useCallback(() => {
    setSchemesLoading(true);
    ListBookingNumberingSchemes({ limit: 100 })
      .then((res) => setSchemes(res.schemes ?? []))
      .catch((e) => setError(errMessage(e, t('booking.admin.numbering.loadError'))))
      .finally(() => setSchemesLoading(false));
  }, [t]);

  useEffect(() => {
    if (tab === 'requests') loadPending();
    if (tab === 'schedule') loadSchedule();
    if (tab === 'all') loadAll();
    if (tab === 'numbering') loadSchemes();
  }, [tab, loadPending, loadSchedule, loadAll, loadSchemes]);

  useEffect(() => {
    if (bookingTypes.length > 0 && !scheduleTypeId) setScheduleTypeId(bookingTypes[0].entity?.id ?? '');
  }, [bookingTypes, scheduleTypeId]);

  const confirmBooking = async (b: Booking) => {
    if (!b.entity) return;
    setBusyId(b.entity.id);
    try {
      await ConfirmBooking(b.entity.type_name || 'booking', b.entity.id, { target: b.entity, resource_id: b.bookable_resource?.id ?? '' });
      loadPending();
    } catch (e) {
      setError(errMessage(e, t('booking.admin.requests.actionError')));
    } finally {
      setBusyId(null);
    }
  };

  const doReject = async () => {
    if (!rejecting?.entity) return;
    setBusyId(rejecting.entity.id);
    try {
      await RejectBooking(rejecting.entity.type_name || 'booking', rejecting.entity.id, {
        target: rejecting.entity,
        resource_id: rejecting.bookable_resource?.id ?? '',
        reason: '',
      });
      setRejecting(null);
      loadPending();
    } catch (e) {
      setError(errMessage(e, t('booking.admin.requests.actionError')));
    } finally {
      setBusyId(null);
    }
  };

  const markNoShow = async (b: Booking) => {
    if (!b.entity) return;
    setBusyId(b.entity.id);
    try {
      await MarkNoShow(b.entity.type_name || 'booking', b.entity.id, { target: b.entity, resource_id: b.bookable_resource?.id ?? '' });
      loadAll();
    } catch (e) {
      setError(errMessage(e, t('booking.admin.requests.actionError')));
    } finally {
      setBusyId(null);
    }
  };

  const doDeleteType = async () => {
    if (!deletingType?.entity) return;
    try {
      await DeleteBookingType(deletingType.entity.id);
      setDeletingType(null);
      loadTypes();
    } catch (e) {
      setError(errMessage(e, t('booking.admin.types.deleteError')));
    }
  };

  const doDeleteScheme = async () => {
    if (!deletingScheme?.entity) return;
    try {
      await DeleteBookingNumberingScheme(deletingScheme.entity.id);
      setDeletingScheme(null);
      loadSchemes();
    } catch (e) {
      setError(errMessage(e, t('booking.admin.numbering.deleteError')));
    }
  };

  // The list payload may be trimmed — load the full record before editing.
  const openEditType = async (bt: BookingTypeDefinition) => {
    let full = bt;
    try {
      full = (await GetBookingType(bt.entity?.id ?? bt.slug)).definition ?? bt;
    } catch {
      full = bt;
    }
    setEditingType(full);
    setTypeDialogOpen(true);
  };

  const openEditScheme = async (s: BookingNumberingScheme) => {
    let full = s;
    try {
      full = (await GetBookingNumberingScheme(s.entity?.id ?? '')).scheme ?? s;
    } catch {
      full = s;
    }
    setEditingScheme(full);
    setSchemeDialogOpen(true);
  };

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-6">
      <div className="flex items-center justify-between">
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('booking.admin.title')}</h1>
        <Link to="/booking" className="ml-auto text-sm text-primary hover:underline">
          {t('booking.catalog.backToCatalog')}
        </Link>
      </div>

      {error && (
        <Alert variant="error">
          {error}
        </Alert>
      )}

      <div role="tablist" className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((tb) => (
          <button
            key={tb}
            role="tab"
            aria-selected={tab === tb}
            onClick={() => setTab(tb)}
            className={`px-3 py-2 text-sm ${tab === tb ? 'border-b-2 border-primary font-medium text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          >
            {t(`booking.admin.tab.${tb}`)}
          </button>
        ))}
      </div>

      {tab === 'requests' && (
        <div className="space-y-2">
          {pendingLoading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
          {!pendingLoading && pending.length === 0 && <p className="text-sm text-muted-foreground">{t('booking.admin.requests.empty')}</p>}
          {pending.map((b) => (
            <div key={b.entity?.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3">
              <div>
                <p className="text-sm font-medium text-foreground">{b.booking_number || t('booking.myBookings.untitled')}</p>
                <p className="text-xs text-muted-foreground">{b.customer?.display_name} · {fmtMoney(b.total_cents, b.currency, locale)}</p>
              </div>
              <div className="flex gap-1.5">
                <Button size="sm" isLoading={busyId === b.entity?.id} onClick={() => confirmBooking(b)}>
                  {t('booking.admin.requests.confirm')}
                </Button>
                <Button size="sm" variant="danger" onClick={() => setRejecting(b)}>
                  {t('booking.admin.requests.reject')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'schedule' && (
        <div className="space-y-3">
          <div className="max-w-xs">
            <Select
              aria-label={t('booking.admin.schedule.bookingType')}
              value={scheduleTypeId}
              onChange={(e) => setScheduleTypeId(e.target.value)}
              options={bookingTypes.map((bt): SelectOption => ({ value: bt.entity?.id ?? '', label: bt.display_name || bt.slug }))}
            />
          </div>
          {scheduleLoading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
          {!scheduleLoading && schedule.length === 0 && <p className="text-sm text-muted-foreground">{t('booking.admin.schedule.empty')}</p>}
          {schedule.map((entry, i) => (
            <div key={i} className="flex items-center justify-between rounded-md border border-border p-3 text-sm">
              <span>{entry.booking?.customer?.display_name || t('booking.review.anonymous')}</span>
              <span className="text-muted-foreground">{fmtDateTime(entry.calendar?.start_time, locale)}</span>
              <Badge variant={BOOKING_STATUS_VARIANT[entry.booking?.status ?? 'BOOKING_STATUS_CONFIRMED']} size="sm">
                {t(`booking.status.${BOOKING_STATUS_KEYS[entry.booking?.status ?? 'BOOKING_STATUS_CONFIRMED']}`)}
              </Badge>
            </div>
          ))}
        </div>
      )}

      {tab === 'all' && (
        <div className="space-y-2">
          {allLoading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
          {!allLoading && allBookings.length === 0 && <p className="text-sm text-muted-foreground">{t('booking.admin.all.empty')}</p>}
          {allBookings.map((b) => {
            const st = b.status ?? 'BOOKING_STATUS_CONFIRMED';
            return (
              <div key={b.entity?.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3">
                <div>
                  <p className="text-sm font-medium text-foreground">{b.booking_number || t('booking.myBookings.untitled')}</p>
                  <p className="text-xs text-muted-foreground">{fmtDateTime(b.created_at, locale)} · {fmtMoney(b.total_cents, b.currency, locale)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={BOOKING_STATUS_VARIANT[st] ?? 'default'} size="sm">
                    {t(`booking.status.${BOOKING_STATUS_KEYS[st]}`)}
                  </Badge>
                  {st === 'BOOKING_STATUS_CONFIRMED' && (
                    <Button size="sm" variant="secondary" isLoading={busyId === b.entity?.id} onClick={() => markNoShow(b)}>
                      {t('booking.admin.requests.markNoShow')}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === 'types' && (
        <div className="space-y-2">
          <div className="flex justify-end">
            <Button size="sm" onClick={() => { setEditingType(null); setTypeDialogOpen(true); }}>
              {t('booking.admin.types.createTitle')}
            </Button>
          </div>
          {typesLoading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
          {!typesLoading && bookingTypes.length === 0 && <p className="text-sm text-muted-foreground">{t('booking.admin.types.empty')}</p>}
          {bookingTypes.map((bt) => (
            <div key={bt.entity?.id} className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <p className="text-sm font-medium text-foreground">{bt.display_name || bt.slug}</p>
                <p className="text-xs text-muted-foreground">{bt.slug}</p>
              </div>
              <div className="flex gap-1.5">
                <Button size="sm" variant="secondary" onClick={() => openEditType(bt)}>
                  {t('common.edit')}
                </Button>
                <Button size="sm" variant="danger" onClick={() => setDeletingType(bt)}>
                  {t('common.delete')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'policies' && <PolicyEditor bookingTypes={bookingTypes} scope={scope} />}

      {tab === 'numbering' && (
        <div className="space-y-2">
          <div className="flex justify-end">
            <Button size="sm" onClick={() => { setEditingScheme(null); setSchemeDialogOpen(true); }}>
              {t('booking.admin.numbering.createTitle')}
            </Button>
          </div>
          {schemesLoading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
          {!schemesLoading && schemes.length === 0 && <p className="text-sm text-muted-foreground">{t('booking.admin.numbering.empty')}</p>}
          {schemes.map((s) => (
            <div key={s.entity?.id} className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <p className="text-sm font-medium text-foreground">{s.name}</p>
                <p className="text-xs text-muted-foreground">{s.prefix}{s.separator}{'0'.repeat(s.counter_digits || 4)}</p>
              </div>
              <div className="flex gap-1.5">
                <Button size="sm" variant="secondary" onClick={() => openEditScheme(s)}>
                  {t('common.edit')}
                </Button>
                <Button size="sm" variant="danger" onClick={() => setDeletingScheme(s)}>
                  {t('common.delete')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <BookingTypeDialog open={typeDialogOpen} onClose={() => setTypeDialogOpen(false)} type={editingType} scope={scope} onSaved={loadTypes} />
      <NumberingSchemeDialog open={schemeDialogOpen} onClose={() => setSchemeDialogOpen(false)} scheme={editingScheme} scope={scope} onSaved={loadSchemes} />

      <ConfirmDialog
        open={!!rejecting}
        title={t('booking.admin.requests.rejectConfirmTitle')}
        description={t('booking.admin.requests.rejectConfirmDescription')}
        destructive
        isConfirming={!!busyId}
        onCancel={() => setRejecting(null)}
        onConfirm={doReject}
      />
      <ConfirmDialog
        open={!!deletingType}
        title={t('booking.admin.types.deleteConfirmTitle')}
        destructive
        onCancel={() => setDeletingType(null)}
        onConfirm={doDeleteType}
      />
      <ConfirmDialog
        open={!!deletingScheme}
        title={t('booking.admin.numbering.deleteConfirmTitle')}
        destructive
        onCancel={() => setDeletingScheme(null)}
        onConfirm={doDeleteScheme}
      />
    </div>
  );
});
